import {
  FC,
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { Gradient } from '__components/Kizen/ScrollFadeContainer';
import { Button, type HoverColor } from '@kizen/kds/Button';
import { Typography } from '@kizen/kds/Typography';
import { useTheme } from 'hooks/useTheme';

interface ExpandingTextAreaProps {
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  disabled?: boolean;
  disabledMessage?: string;
  id?: string;
  handleSubmit: () => void;
  showOneShotGenerationButton?: boolean;
  disableOneShotGenerationButton?: boolean;
  showSubmitButton?: boolean;
  disableSendButton?: boolean;
  disableChatButton?: boolean;
  showChatButton?: boolean;
  oneShotOnly?: boolean;
  initialHeight?: number;
  maxHeight?: number;
  onChatButtonClick?: () => void;
}

export const ExpandingTextArea: FC<ExpandingTextAreaProps> = ({
  value,
  placeholder = '',
  onChange,
  onKeyDown,
  disabled = false,
  disabledMessage,
  id = '',
  handleSubmit,
  showSubmitButton,
  showOneShotGenerationButton,
  showChatButton,
  disableChatButton,
  oneShotOnly = false,
  disableOneShotGenerationButton,
  disableSendButton,
  initialHeight = 50,
  maxHeight = 100,
  onChatButtonClick,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const previousHeightRef = useRef<number>(initialHeight);

  const adjustHeightAndScroll = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to initial height first to get accurate scrollHeight
    textarea.style.height = `${initialHeight}px`;

    // Check if content overflows the initial height
    const contentHeight = textarea.scrollHeight;
    const hasOverflow = contentHeight > initialHeight;

    let newHeight: number;

    if (hasOverflow) {
      // Content overflows, so grow up to maxHeight
      newHeight = Math.min(contentHeight, maxHeight);
    } else {
      // No overflow, stay at initial height
      newHeight = initialHeight;
    }

    const previousHeight = previousHeightRef.current;
    const heightIncreased = newHeight > previousHeight;

    if (parseInt(textarea.style.height) !== newHeight) {
      textarea.style.height = `${newHeight}px`;
      previousHeightRef.current = newHeight;
    }

    // Enable/disable scrolling based on content
    const shouldScroll = contentHeight > maxHeight;
    textarea.style.overflowY = shouldScroll ? 'auto' : 'hidden';

    // Scroll to bottom if height increased or if we're at max height with overflow
    if (heightIncreased || shouldScroll) {
      requestAnimationFrame(() => {
        if (textarea) {
          textarea.scrollTop = textarea.scrollHeight;
        }
      });
    }
  }, [initialHeight, maxHeight]);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
      requestAnimationFrame(adjustHeightAndScroll);
    },
    [onChange, adjustHeightAndScroll]
  );

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    adjustHeightAndScroll();

    resizeObserverRef.current = new ResizeObserver(() => {
      adjustHeightAndScroll();
    });

    resizeObserverRef.current.observe(textarea);

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [adjustHeightAndScroll]);

  // Adjust height when value changes externally
  useEffect(() => {
    requestAnimationFrame(adjustHeightAndScroll);
  }, [value, adjustHeightAndScroll]);

  const { getSemanticColor } = useTheme();

  return (
    <div
      className={`relative h-full border-t border-b border-l-0 border-r-0 border-solid border-border-medium-light flex items-end pr-[10px] gap-[5px] ${disabled ? 'bg-border-medium-light cursor-not-allowed' : ''}`}
    >
      {!disabled ? (
        <div className="absolute top-0 left-0 w-[calc(100%-50px)] pointer-events-none z-[2]">
          <Gradient
            fadeStart={0}
            fadeLength={25}
            color="var(--color-background-white)"
            position="top"
            fadeIn
            zIndex={2}
          />
        </div>
      ) : null}

      {!disabled ? (
        <div className="absolute bottom-0 left-0 w-[calc(100%-50px)] pointer-events-none z-[2]">
          <Gradient
            fadeStart={0}
            fadeLength={25}
            color="var(--color-background-white)"
            position="bottom"
            fadeIn
            zIndex={2}
          />
        </div>
      ) : null}
      {disabledMessage && disabled ? (
        <div
          className="pt-[18px] pl-[15px] pr-[5px] w-full leading-[1.25] cursor-not-allowed truncate"
          style={{
            height: initialHeight,
            maxHeight,
          }}
        >
          <Typography truncate color="font-placeholder">
            {disabledMessage}
          </Typography>
        </div>
      ) : (
        <textarea
          ref={textareaRef}
          id={id}
          value={value}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="border-none resize-none focus:outline-none font-sans text-[14px] font-normal text-font-primary py-[15px] pl-[15px] pr-[5px] w-full kds-expanding-scrollbar overflow-x-hidden transition-none leading-[1.25] disabled:bg-border-medium-light disabled:text-border-medium-light disabled:cursor-not-allowed placeholder-font-placeholder"
          style={{
            height: initialHeight,
            maxHeight,
          }}
        />
      )}
      {showOneShotGenerationButton ? (
        <>
          <div className="w-[25px] h-[30px]">
            <div
              className={
                oneShotOnly
                  ? 'absolute bottom-[5px] right-[15px] text-button-tertiary-default'
                  : 'absolute bottom-[5px] right-[53px] text-button-tertiary-default'
              }
            >
              <Button
                rightIcon="action-generate"
                variant="text"
                color="inherit"
                hoverColor={getSemanticColor('ai-user-bg') as HoverColor}
                onClick={handleSubmit}
                showTooltip
                rightIconSettings={{
                  tooltipDescriptionOverride: 'Build Now',
                }}
                disabled={disabled || disableOneShotGenerationButton}
              />
            </div>
          </div>
          {!oneShotOnly ? (
            <div className="h-full w-[1px] bg-border-medium-light mr-[3px]" />
          ) : null}
        </>
      ) : null}
      {showChatButton && !oneShotOnly ? (
        <div className="w-[30px] h-[30px]">
          <div className="absolute bottom-[5px] right-[15px] text-button-tertiary-default">
            <Button
              rightIcon="action-start-chat"
              variant="text"
              color="inherit"
              hoverColor={getSemanticColor('ai-user-bg') as HoverColor}
              onClick={() => {
                if (onChatButtonClick) {
                  onChatButtonClick();
                }
              }}
              disabled={disabled || disableChatButton}
            />
          </div>
        </div>
      ) : showSubmitButton && !oneShotOnly ? (
        <div className="w-[30px] h-[30px]">
          <div className="absolute bottom-[5px] right-[15px] text-button-tertiary-default">
            <Button
              rightIcon="action-send-message"
              variant="text"
              color="inherit"
              hoverColor={getSemanticColor('ai-user-bg') as HoverColor}
              onClick={handleSubmit}
              disabled={disabled || disableSendButton}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};
